// Phase Q.3 — Observable for the pending-sales count + status.
//
// The connection-state badge surfaces "N pending sales" while sales are
// queued offline. This store keeps that number reactive without each
// consumer running its own polling loop. Subscribers receive the
// current snapshot on subscribe and every time the queue changes.

import { connectionStateStore } from './connection-state';
import {
  deletePendingSale,
  listPendingSales,
  type PendingSale,
  updatePendingSale,
} from '../database/sale-outbox';
import type { EasyPosApiClient } from '../api/easypos-api';
import type { ISale } from '../api/model/requests';

export interface PendingSalesSnapshot {
  shopName: string | null;
  // Sales still IN FLIGHT (queued or replaying). They drain + clear on their
  // own when the network returns — this is the "syncing" count the banner shows.
  count: number;
  // Sales the server REJECTED on replay (status 'failed', e.g. the menu item
  // was deleted, or the trading day/shift closed while it sat queued). These
  // never drain on their own; they need an operator decision (retry / discard).
  // Surfaced separately so `count` can reach zero and the operator gets told.
  failed: PendingSale[];
  // Every entry (syncing + failed) in FIFO submission order.
  sales: PendingSale[];
}

export type PendingSalesListener = (snap: PendingSalesSnapshot) => void;

export class PendingSalesStore {
  private _snapshot: PendingSalesSnapshot = {
    shopName: null,
    count: 0,
    failed: [],
    sales: [],
  };
  private _listeners = new Set<PendingSalesListener>();
  private _activeShop: string | null = null;
  private _draining = false;
  private _drainerApiClient: EasyPosApiClient | null = null;
  // Listen to connection state so we drain when the network returns.
  private _connectionUnsub?: () => void;

  // Initialise. Called once at app start; on user logout you can
  // attach(null) to clear state without tearing the singleton down.
  attachShop(
    shopName: string | null,
    apiClient: EasyPosApiClient | null
  ): void {
    this._activeShop = shopName;
    this._drainerApiClient = apiClient;
    this._snapshot = this._snapshotFrom([]);
    this._emit();
    if (shopName && apiClient) {
      void this.refresh();
      this._subscribeToConnection();
    } else {
      this._connectionUnsub?.();
      this._connectionUnsub = undefined;
    }
  }

  // Re-read the outbox state from IDB. Call after enqueue / drain so
  // subscribers see the new count. Best-effort — failures leave the
  // existing snapshot in place.
  async refresh(): Promise<void> {
    if (!this._activeShop) return;
    try {
      const list = await listPendingSales(this._activeShop);
      this._snapshot = this._snapshotFrom(list);
      this._emit();
    } catch {
      // ignore
    }
  }

  // Build a snapshot from a raw outbox list: `count` is the in-flight
  // (queued/replaying) total; `failed` is the rejected set the operator must
  // resolve. Keeping this in one place keeps the two counts consistent across
  // refresh + the mid-drain status flips.
  private _snapshotFrom(list: PendingSale[]): PendingSalesSnapshot {
    return {
      shopName: this._activeShop,
      count: list.filter((s) => s.status !== 'failed').length,
      failed: list.filter((s) => s.status === 'failed'),
      sales: list,
    };
  }

  get snapshot(): PendingSalesSnapshot {
    return this._snapshot;
  }

  subscribe(listener: PendingSalesListener): () => void {
    this._listeners.add(listener);
    listener(this._snapshot);
    return () => this._listeners.delete(listener);
  }

  // Trigger a drain pass right now (e.g. user tapped a "retry now"
  // button on the pending-sales pill). Idempotent — concurrent calls
  // collapse to one in-flight drain.
  async drainNow(): Promise<void> {
    if (this._draining) return;
    if (!this._activeShop || !this._drainerApiClient) return;
    this._draining = true;
    try {
      await this._drainOnce();
    } finally {
      this._draining = false;
    }
  }

  // Operator chose to RETRY a failed sale (e.g. they reopened the day, or
  // restored a deleted item). Flip it back to 'queued', clear the stale error,
  // and kick a drain. The idempotency key is unchanged, so if the original
  // actually landed on a prior attempt the server returns it rather than
  // double-recording.
  async retryFailed(idempotencyKey: string): Promise<void> {
    if (!this._activeShop) return;
    const shopName = this._activeShop;
    const entry = this._snapshot.failed.find(
      (s) => s.idempotencyKey === idempotencyKey
    );
    if (!entry) return;
    const requeued: PendingSale = {
      ...entry,
      status: 'queued',
      lastError: undefined,
    };
    await updatePendingSale(shopName, requeued);
    await this.refresh();
    await this.drainNow();
  }

  // Operator chose to DISCARD a failed sale — accepting it will NOT be
  // recorded (real money; the UI gates this behind a confirm). Drop the outbox
  // row for good.
  async discardFailed(idempotencyKey: string): Promise<void> {
    if (!this._activeShop) return;
    await deletePendingSale(this._activeShop, idempotencyKey);
    await this.refresh();
  }

  private _subscribeToConnection(): void {
    this._connectionUnsub?.();
    this._connectionUnsub = connectionStateStore.subscribe((snap) => {
      // Fire a drain when we transition back to online. We don't gate
      // strictly on "was offline before" — drainOnce is a no-op when
      // the outbox is empty, so an extra invocation is cheap.
      if (snap.state === 'online') {
        void this.drainNow();
      }
    });
  }

  private async _drainOnce(): Promise<void> {
    const shopName = this._activeShop;
    const api = this._drainerApiClient;
    if (!shopName || !api) return;
    const queue = await listPendingSales(shopName);
    if (queue.length === 0) return;
    // Submission order — FIFO.
    for (const entry of queue) {
      if (entry.status === 'failed') {
        // Failed entries stay in the queue until the operator dismisses
        // them. Skip on this drain.
        continue;
      }
      const replaying: PendingSale = { ...entry, status: 'replaying' };
      await updatePendingSale(shopName, replaying);
      this._snapshot = this._snapshotFrom(
        this._snapshot.sales.map((s) =>
          s.idempotencyKey === entry.idempotencyKey ? replaying : s
        )
      );
      this._emit();
      // Send through the regular API client. The payload already has
      // the idempotencyKey baked in (set at enqueue time), so the
      // server returns the existing sale if this entry has already
      // landed on a prior drain attempt that was interrupted.
      let result;
      try {
        result = await api.createSale(shopName, entry.payload);
      } catch {
        result = { success: false as const, error: 'Network failure' };
      }
      if (result.success && result.data) {
        // Server accepted (or recognised as already-recorded). Drop
        // the outbox row.
        await deletePendingSale(shopName, entry.idempotencyKey);
        await this._announceDrained(entry.idempotencyKey, result.data);
      } else if (this._isNetworkFailure(result.error)) {
        // Still no network — leave the entry queued + restore status.
        // Future Q.1 reconnect or manual drainNow will retry.
        const restored: PendingSale = { ...entry, status: 'queued' };
        await updatePendingSale(shopName, restored);
        await this.refresh();
        return; // Stop the drain; everything after this is also blocked.
      } else {
        // Server rejected the payload (e.g. menu item deleted, validation
        // change). Park as 'failed' so the operator can decide.
        const failed: PendingSale = {
          ...entry,
          status: 'failed',
          lastError: result.error ?? 'Server rejected this sale',
        };
        await updatePendingSale(shopName, failed);
        await this.refresh();
      }
    }
    await this.refresh();
  }

  private async _announceDrained(
    idempotencyKey: string,
    sale: ISale
  ): Promise<void> {
    // Custom event so the active-orders board (or anything else
    // interested) can refresh in response. The component listening
    // doesn't need to know about the outbox internals.
    try {
      const event = new CustomEvent('sale-outbox-drained', {
        detail: { idempotencyKey, sale },
      });
      window.dispatchEvent(event);
    } catch {
      // ignore (window unavailable in tests/SSR)
    }
  }

  private _isNetworkFailure(error?: string): boolean {
    if (!error) return false;
    const e = error.toLowerCase();
    return (
      e.includes('network') || e.includes('timeout') || e.includes('fetch')
    );
  }

  private _emit(): void {
    for (const l of this._listeners) l(this._snapshot);
  }
}

export const pendingSalesStore = new PendingSalesStore();
